import { useCallback, useEffect, useRef, useState } from "react";
import { getCtaServiceStatus } from "components/Environment/utils";
import { Environment } from "components/Environment/utils/types";

export const HEALTH_CHECK_TIMEOUT_MS = 30_000;
/** Longer timeout used when the health check status is "unknown" (hasn't resolved yet). */
export const HEALTH_CHECK_UNKNOWN_TIMEOUT_MS = 5 * 60 * 1000;

interface UseHealthCheckResult {
  healthCheckFailed: boolean;
  reset: () => void;
}

/**
 * Manages health check timeout state for the deploying panel.
 * Sets `healthCheckFailed` when the service stays unhealthy or unresolved too long.
 */
export function useHealthCheck(
  isHealthCheckStep: boolean,
  environment: Environment | undefined,
): UseHealthCheckResult {
  const [failedAttemptId, setFailedAttemptId] = useState<number | null>(null);
  const [healthCheckAttemptId, setHealthCheckAttemptId] = useState(0);
  const healthCheckStartTimeRef = useRef<number | null>(null);
  // Tracks the latest CTA health-check status so the timeout timer (which runs
  // on an interval, not inside the environment effect) can read a fresh value.
  const ctaHealthStatusRef = useRef<string | null>(null);
  const healthCheckFailed =
    isHealthCheckStep && failedAttemptId === healthCheckAttemptId;

  useEffect(() => {
    const nextStatus = getCtaServiceStatus(environment);
    const previousStatus = ctaHealthStatusRef.current;
    ctaHealthStatusRef.current = nextStatus;

    if (
      isHealthCheckStep &&
      healthCheckStartTimeRef.current !== null &&
      previousStatus !== nextStatus
    ) {
      healthCheckStartTimeRef.current = Date.now();
    }
  }, [environment, isHealthCheckStep]);

  // Record the timestamp when the health check step first becomes active, then
  // poll every second to enforce timeouts. Using setInterval rather than
  // checking inside the environment effect because the environment stops
  // changing once health check status stabilises at "unhealthy", so the effect
  // would never re-fire.
  useEffect(() => {
    if (isHealthCheckStep) {
      if (healthCheckStartTimeRef.current === null) {
        healthCheckStartTimeRef.current = Date.now();
      }
    } else {
      if (
        healthCheckStartTimeRef.current !== null ||
        failedAttemptId !== null
      ) {
        healthCheckStartTimeRef.current = null;
        setFailedAttemptId(null);
        setHealthCheckAttemptId((attemptId) => attemptId + 1);
      }
      return;
    }

    if (healthCheckFailed) return;

    const timer = setInterval(() => {
      if (!healthCheckFailed && healthCheckStartTimeRef.current !== null) {
        const elapsed = Date.now() - healthCheckStartTimeRef.current;
        // "unknown" means the health check API hasn't resolved yet — give it
        // longer before giving up. Any other status (including "unhealthy") uses
        // the shorter timeout.
        const isUnknown = ctaHealthStatusRef.current === "unknown";
        const timeout = isUnknown
          ? HEALTH_CHECK_UNKNOWN_TIMEOUT_MS
          : HEALTH_CHECK_TIMEOUT_MS;
        if (elapsed > timeout) {
          setFailedAttemptId(healthCheckAttemptId);
        }
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [
    isHealthCheckStep,
    healthCheckFailed,
    healthCheckAttemptId,
    failedAttemptId,
  ]);

  const reset = useCallback(() => {
    setFailedAttemptId(null);
    healthCheckStartTimeRef.current = null;
    setHealthCheckAttemptId((attemptId) => attemptId + 1);
    ctaHealthStatusRef.current = null;
  }, []);

  return { healthCheckFailed, reset };
}
